import {
  createContext,
  ReactNode,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { debounce } from "lodash";
import type {
  RenderConfigClip,
  RenderConfig,
} from "@memento/types/render-config";
import { NormalizedSubscription } from "@memento/types/subscription";
import { type CompilationV3 } from "@memento/types/compilation";
import { type Segment } from "@memento/types/segment";
import { segmentToRenderConfigClip } from "@memento/library/segment";
import { getIsMyCompilationStale } from "@memento/library/compilation";
import { Card } from "@memento/types/card";
import {
  type SubSceneSkipToFrame,
  getSubSceneSkipToFrames,
} from "@memento/library/render";
import { toastError } from "src/libs/toast-lib";
import { LoadingStatus } from "src/libs/fetcher";
import {
  usePlayerHelper,
  UsePlayerHelperType,
} from "./components/PlayerHelper";
import { useGreetings, UseGreetingsType } from "./components/Greeting";
import { useMusic, UseMusicType } from "./components/Music";
import { useTheme, UseThemeType } from "./components/Theme";
import { useBranding, UseBrandingType } from "./components/Branding";
import { useClip, UseClipType } from "./components/Clips";
import { UseCardSegmentsResult } from "src/hooks/useCardSegments";
import { isBaseError } from "src/hooks/createAuthenticatedMutationHook";
import errors from "src/errors";
import { RendererPageInfo } from "src/types/renderer";
import { useFeatureFlags } from "src/contexts/FeatureFlagProvider";
import {
  type UseDragAndDropResult,
  useDragAndDrop,
} from "src/hooks/useDragAndDrop";
import { useUpdateCompilation } from "src/hooks/useUpdateCompilation";
import { RenderEnabledStatus } from "src/hooks/useCompilationResources";
import { UseCardResult } from "src/hooks/useCard";

type GetRendererPageUrlArgs = RendererPageInfo & {
  cardId: string;
  cardUrl: string;
};

export type GetRendererPageUrlFn = (args: GetRendererPageUrlArgs) => string;

export type SetUploadedLogo = (
  originalUploadUrl: string,
  urls: string[]
) => void;

export type RenderStartedStatus =
  | never
  // The render has not been started yet
  | { status: "not-started" }
  // The render has been requested but we're waiting for it to start
  | { status: "pending" }
  // The render has started successfully (this does not indicate whether it has completed)
  | { status: "success" }
  // The render start request failed
  | { status: "failure"; error: string };

type RendererContextValueType = {
  editor: RenderConfig;
  compilation: CompilationV3;
  status: LoadingStatus;
  compilationId: string;
  getRendererPageUrl: GetRendererPageUrlFn;
  playerHelper: UsePlayerHelperType;
  greetingFunctions: UseGreetingsType;
  musicFunctions: UseMusicType;
  themeFunctions: UseThemeType;
  brandingFunctions: UseBrandingType;
  clipFunctions: UseClipType;
  setUploadedLogo: SetUploadedLogo;
  queryStrategy: QueryStrategy;
  clipsDragAndDrop: UseDragAndDropResult<RenderConfigClip> & {
    draggingItemIndex?: number;
  };
  renderEnabledStatus: RenderEnabledStatus;
  renderStartedStatus: RenderStartedStatus;
  allSegments: Segment[];
  subSceneSkipToFrames: SubSceneSkipToFrame[];
  subscription: NormalizedSubscription;
  card: Card;
};

export const useEditor = () => {
  const context = useContext(RendererContext);
  if (!context) {
    throw new Error("useEditor must be used within a RendererProvider");
  }

  const {
    editor,
    compilation,
    status,
    compilationId,
    getRendererPageUrl,
    playerHelper,
    greetingFunctions,
    musicFunctions,
    themeFunctions,
    brandingFunctions,
    clipFunctions,
    setUploadedLogo,
    queryStrategy,
    clipsDragAndDrop,
    renderEnabledStatus,
    renderStartedStatus,
    allSegments,
    subSceneSkipToFrames,
    subscription,
    card,
  } = context;

  return {
    editor,
    fps: editor.videoFrame.fps,
    width: editor.videoFrame.width,
    height: editor.videoFrame.height,
    status,
    compilationId,
    compilation,
    getRendererPageUrl,
    setUploadedLogo,
    queryStrategy,
    clipsDragAndDrop,
    renderEnabledStatus,
    renderStartedStatus,
    allSegments,
    ...playerHelper,
    ...greetingFunctions,
    ...musicFunctions,
    ...themeFunctions,
    ...brandingFunctions,
    ...clipFunctions,
    subSceneSkipToFrames,
    subscription,
    card,
  };
};

export type SetEditor = React.Dispatch<React.SetStateAction<RenderConfig>>;

const RendererContext = createContext<RendererContextValueType | null>(null);

function setClipsFromSegments(renderConfig: RenderConfig, segments: Segment[]) {
  return {
    ...renderConfig,
    clips: segments.map((segment) => segmentToRenderConfigClip(segment)),
  };
}

function callProxy<FnRet>(fn: () => FnRet): FnRet {
  return fn();
}

type QueryStrategy =
  | never
  // "live" means that queries will be made and changes will be saved to the
  // API
  | "live"
  // "offline" means that data will be provided statically and changes will be
  // saved in state but will not be persisted to the API
  | "offline";

type RendererProviderProps = {
  getRendererPageUrl: GetRendererPageUrlFn;
  setCompilation: React.Dispatch<React.SetStateAction<CompilationV3>>;
  compilation: CompilationV3;
  reloadCompilation: () => void;
  segmentsModel: UseCardSegmentsResult;
  cardModel: UseCardResult;
  children: ReactNode;
  queryStrategy?: QueryStrategy;
  renderEnabledStatus: RenderEnabledStatus;
  /** The status of the render started request made in this session. This
   * doesn't track any status from the server, just an internal, client-side
   * state.
   */
  renderStartedStatus: RenderStartedStatus;
  subscription: NormalizedSubscription;
};

export const RendererProvider = ({
  getRendererPageUrl,
  setCompilation,
  compilation,
  reloadCompilation,
  segmentsModel,
  cardModel,
  queryStrategy = "live",
  children,
  renderEnabledStatus,
  renderStartedStatus,
  subscription,
}: RendererProviderProps) => {
  const { reloadFeatureFlags } = useFeatureFlags();

  // Warn before leaving/refreshing while segment saves are still in flight.
  // Saves are throttled and persisted one at a time, so navigating away can
  // drop queued changes (e.g. clips not yet moved to outtakes on the server).
  useEffect(() => {
    if (!segmentsModel.isSavingSegments) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Some browsers require returnValue to be set to trigger the prompt.
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [segmentsModel.isSavingSegments]);

  // We could just directly wrap updateCompilation.trigger, but we'd have to
  // update that whenever updateCompilation.trigger changes, which would reset
  // the debounce. This function will never change and we can pass whatever
  // function we want to it
  const [debouncedUpdateCompilationProxy] = useState(() => {
    return debounce(callProxy, 500);
  });

  const updateCompilation = useUpdateCompilation({
    concurrencyBehavior: "abort",
    onSuccess: (data) => {
      if (
        getIsMyCompilationStale(
          data.renderRequest.status.lastSaveRequest,
          compilation.renderRequest.status.lastSaveRequest,
          // sentAt will be equal if this response is our last request, so we
          // don't want to consider that stale
          { strategy: "equal-sent-at-is-not-stale" }
        )
      ) {
        setCompilation(data);
      }
    },
    onFailure: (error) => {
      if (isBaseError(error) && error.code === "out-of-order") {
        if (
          getIsMyCompilationStale(
            error.compilation.renderRequest.status.lastSaveRequest,
            compilation.renderRequest.status.lastSaveRequest,
            // sentAt will be equal if this response is our last request, so we
            // don't want to consider that stale
            { strategy: "equal-sent-at-is-not-stale" }
          )
        ) {
          setCompilation(error.compilation);
        }

        return;
      } else if (isBaseError(error) && error.code === "feature-disabled") {
        toastError(errors.RENDER_V3_DISABLED);

        // don't redirect to the card dashboard because we don't know what
        // page we're on; it could create an infinite redirect loop

        // reload feature flags because they must be stale if we've gotten to
        // this point
        reloadFeatureFlags();

        return;
      } else if (isBaseError(error) && error.code === "render-config-reset") {
        setCompilation(error.compilation);

        toastError(errors.RENDER_CONFIG_RESET_TOAST, { autoClose: false });

        return;
      } else if (error instanceof DOMException && error.name === "AbortError") {
        // do nothing, we just aborted the request
        return;
      } else {
        toastError(
          "Something went wrong while saving your changes. Your settings have been reloaded. Please check that everything looks correct and try again."
        );

        reloadCompilation();

        return;
      }
    },
  });

  const handleEditorChange: SetEditor = (valueOrCallback) => {
    setCompilation((prev) => {
      const newRenderConfig =
        valueOrCallback instanceof Function
          ? valueOrCallback(prev.renderRequest.renderConfig)
          : valueOrCallback;

      const themeHasChanged =
        newRenderConfig.themeId !== prev.renderRequest.renderConfig.themeId;

      const lastSequenceNumber =
        prev.renderRequest.status.lastSaveRequest?.sequenceNumber || 0;

      const lastSaveRequest = {
        sequenceNumber: lastSequenceNumber + 1,
        sentAt: Date.now(),
      };

      if (queryStrategy === "live") {
        debouncedUpdateCompilationProxy(() =>
          updateCompilation.trigger(
            { compilationId: compilation.compilationId },
            {
              ...(themeHasChanged
                ? {
                    // Only save the occasionId and theme if the them has changed.
                    // This is mostly so that we only save the occasionId to the API
                    // when the theme changes. If we saved it whenever occasionId
                    // changed, we might end up with a selected occasion that
                    // doesn't contain the selected theme.
                    theme: {
                      occasionId: newRenderConfig.occasionId,
                      themeId: newRenderConfig.themeId,
                    },
                  }
                : {}),
              sequenceNumber: lastSaveRequest.sequenceNumber,
              sentAt: lastSaveRequest.sentAt,
              music: {
                enabled: newRenderConfig.music.enabled,
                volumeDuringClips: newRenderConfig.music.volumeDuringClips,
                songId: newRenderConfig.music.song.id,
              },
              introGreeting: {
                enabled: newRenderConfig.introGreeting.enabled,
                messages: newRenderConfig.introGreeting.messages,
              },
              outroGreeting: {
                enabled: newRenderConfig.outroGreeting.enabled,
                messages: newRenderConfig.outroGreeting.messages,
              },
              logo: {
                enabled: newRenderConfig.logo.enabled,
                size: newRenderConfig.logo.size,
              },
              branding: {
                enabled: newRenderConfig.branding?.enabled ?? true,
              },
              userInput: newRenderConfig.userInput || {},
              faceCollageEnabled: newRenderConfig.faceCollageEnabled ?? true,
            }
          )
        );
      }

      return {
        ...prev,
        renderRequest: {
          ...prev.renderRequest,
          status: {
            ...prev.renderRequest.status,
            lastSaveRequest,
          },
          renderConfig: newRenderConfig,
        },
      };
    });
  };

  const setUploadedLogo: SetUploadedLogo = (originalUploadUrl, urls) => {
    setCompilation((prev) => ({
      ...prev,
      renderRequest: {
        ...prev.renderRequest,
        renderConfig: {
          ...prev.renderRequest.renderConfig,
          logo: {
            ...prev.renderRequest.renderConfig.logo,
            originalUploadUrl,
            urls,
          },
        },
      },
    }));
  };

  const editor = useMemo(
    () =>
      setClipsFromSegments(
        compilation.renderRequest.renderConfig,
        segmentsModel.activeSegments
      ),
    [compilation, segmentsModel.activeSegments]
  );

  const clipsDragAndDrop = useDragAndDrop({
    moveItem: ({ oldIndex, newIndex }) =>
      segmentsModel.moveActiveSegment({
        // assuming index === sortOrder
        oldSortOrder: oldIndex,
        // assuming index === sortOrder
        newSortOrder: newIndex,
      }),
    getItemFromId: (segmentId) =>
      editor.clips.find((c) => c.segmentId === segmentId),
  });

  const draggingClipIndex = useMemo(() => {
    return editor.clips.findIndex(
      (c) => c.segmentId === clipsDragAndDrop.draggingItem?.segmentId
    );
  }, [clipsDragAndDrop.draggingItem, editor]);

  const themeFunctions = useTheme(editor, handleEditorChange);
  const playerHelper = usePlayerHelper(editor, themeFunctions.theme);
  const greetingFunctions = useGreetings(editor, handleEditorChange);
  const musicFunctions = useMusic(handleEditorChange);
  const brandingFunctions = useBranding(handleEditorChange);
  const subSceneSkipToFrames = getSubSceneSkipToFrames(
    editor,
    themeFunctions.theme
  );
  const clipFunctions = useClip(
    editor,
    segmentsModel,
    cardModel,
    subSceneSkipToFrames
  );

  useEffect(() => {
    themeFunctions.preFetchThemeAssets(themeFunctions.theme!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RendererContext.Provider
      value={{
        editor,
        compilation,
        status: updateCompilation.status,
        compilationId: compilation.compilationId,
        getRendererPageUrl,
        playerHelper,
        greetingFunctions,
        musicFunctions,
        themeFunctions,
        brandingFunctions,
        clipFunctions,
        setUploadedLogo,
        queryStrategy,
        clipsDragAndDrop: {
          ...clipsDragAndDrop,
          draggingItemIndex: draggingClipIndex,
        },
        renderEnabledStatus,
        renderStartedStatus,
        allSegments: segmentsModel.allSegments,
        subSceneSkipToFrames,
        subscription,
        card: cardModel.card,
      }}
    >
      {children}
    </RendererContext.Provider>
  );
};
